import React, { useState, useEffect } from 'react';
import { Building2, Save, CreditCard, Percent } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import Pagination from '../../Components/common/Pagination';
import { TableSkeleton } from '../../Components/common/Skeleton';

const PaymentSettings = () => {
    const [hospitals, setHospitals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    // Global settings state
    const [globalGst, setGlobalGst] = useState('');
    const [globalPlatformFee, setGlobalPlatformFee] = useState('');

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });

    useEffect(() => {
        fetchHospitals();
    }, [pagination.page]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const fetchHospitals = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/hospitals?page=${pagination.page}&limit=${pagination.limit}`);
            if (response.data?.success) {
                setHospitals(response.data.data.hospitals || response.data.data || []);
                if (response.data.data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.data.data.pagination.total || 0,
                        pages: response.data.data.pagination.pages || 0
                    }));
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to fetch hospitals');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyToAll = async () => {
        if (!globalGst && !globalPlatformFee) {
            toast.error('Please enter at least one global value');
            return;
        }

        setUpdating(true);
        try {
            await api.put('/hospitals/bulk/payment-settings', {
                isAll: true,
                gstPercent: globalGst ? parseInt(globalGst) : undefined,
                platformFeePercent: globalPlatformFee ? parseInt(globalPlatformFee) : undefined
            });
            toast.success('Settings applied to all hospitals successfully');
            setGlobalGst('');
            setGlobalPlatformFee('');
            fetchHospitals(); // Refresh to show new values
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to apply global settings');
        } finally {
            setUpdating(false);
        }
    };

    const handleHospitalUpdate = async (hospitalId, gst, fee) => {
        setUpdating(true);
        try {
            await api.put('/hospitals/bulk/payment-settings', {
                hospitalIds: [hospitalId],
                isAll: false,
                gstPercent: gst,
                platformFeePercent: fee
            });
            toast.success('Hospital settings updated');
            fetchHospitals();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update hospital settings');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-[#143E78]">Payment Configuration</h1>
                    <p className="text-sm font-bold text-gray-500">Manage GST and Platform Fees globally or per hospital</p>
                </div>
            </div>

            {/* Global Settings Card */}
            <div className="bg-white rounded-[32px] p-6 sm:p-8 shadow-sm border border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-lg font-black text-[#143E78] mb-1">Global Settings</h2>
                        <p className="text-xs font-bold text-gray-500">Apply these percentages to all registered hospitals</p>
                    </div>

                    <div className="flex items-end gap-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Global GST Rate (%)</label>
                            <input 
                                type="number" 
                                value={globalGst}
                                onChange={(e) => setGlobalGst(e.target.value)}
                                placeholder="e.g. 18"
                                className="w-28 px-4 py-2.5 bg-gray-50 border-2 border-gray-100 rounded-xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Platform Fee (%)</label>
                            <input 
                                type="number" 
                                value={globalPlatformFee}
                                onChange={(e) => setGlobalPlatformFee(e.target.value)}
                                placeholder="e.g. 5"
                                className="w-28 px-4 py-2.5 bg-gray-50 border-2 border-gray-100 rounded-xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all"
                            />
                        </div>
                        <button 
                            onClick={handleApplyToAll}
                            disabled={updating || (!globalGst && !globalPlatformFee)}
                            className="bg-[#143E78] text-white px-6 py-2.5 rounded-xl text-xs font-black tracking-widest uppercase hover:bg-blue-900 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            <Save size={14} /> Apply to All
                        </button>
                    </div>
                </div>
            </div>

            {/* Individual Hospitals Table */}
            <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-50">
                    <h2 className="text-lg font-black text-[#143E78]">Hospital Specific Settings</h2>
                </div>
                
                {loading ? (
                    <div className="p-6">
                        <TableSkeleton rows={10} />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Hospital Details</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">City</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">GST Rate (%)</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Platform Fee (%)</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {hospitals.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-sm font-bold text-gray-400">No hospitals found</td>
                                </tr>
                            ) : (
                                hospitals.map((hospital) => (
                                    <HospitalRow 
                                        key={hospital._id} 
                                        hospital={hospital} 
                                        onSave={(gst, fee) => handleHospitalUpdate(hospital._id, gst, fee)}
                                        updating={updating}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                )}
            </div>
            
            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest order-2 sm:order-1">
                        Displaying {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
                    </div>
                    <div className="order-1 sm:order-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.pages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const HospitalRow = ({ hospital, onSave, updating }) => {
    const [gst, setGst] = useState(hospital.gstPercent || 18);
    const [fee, setFee] = useState(hospital.platformFeePercent || 5);

    const hasChanged = gst !== (hospital.gstPercent || 18) || fee !== (hospital.platformFeePercent || 5);

    return (
        <tr className="hover:bg-gray-50/50 transition-colors">
            <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Building2 size={16} className="text-blue-600" />
                    </div>
                    <div>
                        <p className="text-sm font-black text-[#143E78]">{hospital.hospitalName}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">ID: {hospital._id.slice(-6)}</p>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4">
                <span className="text-xs font-bold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {hospital.address?.city || 'N/A'}
                </span>
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-2">
                    <input 
                        type="number"
                        value={gst}
                        onChange={(e) => setGst(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1.5 bg-white border-2 border-gray-100 rounded-lg text-sm font-bold text-center text-[#143E78] outline-none focus:border-blue-500"
                    />
                    <Percent size={12} className="text-gray-400" />
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-2">
                    <input 
                        type="number"
                        value={fee}
                        onChange={(e) => setFee(parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1.5 bg-white border-2 border-gray-100 rounded-lg text-sm font-bold text-center text-[#143E78] outline-none focus:border-blue-500"
                    />
                    <Percent size={12} className="text-gray-400" />
                </div>
            </td>
            <td className="px-6 py-4 text-right">
                <button
                    onClick={() => onSave(gst, fee)}
                    disabled={updating || !hasChanged}
                    className={`px-4 py-1.5 rounded-lg text-xs font-black tracking-widest uppercase transition-all flex items-center gap-1.5 ml-auto ${
                        hasChanged 
                            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200 hover:bg-emerald-600 hover:-translate-y-0.5' 
                            : 'bg-gray-100 text-gray-400'
                    }`}
                >
                    <Save size={12} /> Save
                </button>
            </td>
        </tr>
    );
};

export default PaymentSettings;
